import { dispatchMapWindowLayoutChange } from "./map-control-events.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import { resolveMapPageShell } from "./map-shell-lifecycle.js";
import { createMapWindowManager } from "./map-window-manager.js";

const WINDOW_MANAGER_TAG_NAME = "fishymap-window-manager";
const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyMapWindowManagerElement extends HTMLElementBase {
  constructor() {
    super();
    this._shell = null;
    this._manager = null;
    this._mapShellProjection = null;
  }

  connectedCallback() {
    this.hidden = true;
    if (this._manager) {
      this._manager.applyFromSignals?.();
      return;
    }
    const shell = this.closest?.("#map-page-shell") || resolveMapPageShell(globalThis);
    if (!shell) {
      return;
    }
    this._shell = shell;
    this._manager = createMapWindowManager({
      shell,
      onWindowLayoutChange: (detail) => this.dispatchWindowLayoutChange(detail),
      getSignals: () => ({
        _map_ui: this._mapShellProjection?.ui || {},
      }),
    });
    this._manager.applyFromSignals?.();
  }

  get mapShellProjection() {
    return this._mapShellProjection;
  }

  set mapShellProjection(value) {
    const projection = assertMapShellProjectionV1(value);
    const previousProjection = this._mapShellProjection;
    if (previousProjection) {
      if (projection.revision < previousProjection.revision) {
        throw new RangeError("Map shell projection revision cannot regress");
      }
      if (projection.revision === previousProjection.revision) {
        return;
      }
    }
    this._mapShellProjection = projection;
    if (!previousProjection || projection.ui !== previousProjection.ui) {
      this._manager?.applyFromSignals?.();
    }
  }

  applyFromSignals() {
    this._manager?.applyFromSignals?.();
  }

  dispatchWindowLayoutChange(detail) {
    return dispatchMapWindowLayoutChange(this, detail);
  }

  bringToFront(windowId) {
    return this._manager?.bringToFront?.(windowId) ?? 20;
  }

  highestWindowZIndex() {
    return this._manager?.highestWindowZIndex?.() ?? 20;
  }

  disconnectedCallback() {
    this._manager?.destroy?.();
    this._manager = null;
    this._shell = null;
  }
}

export function registerFishyMapWindowManagerElement(registry = globalThis.customElements) {
  if (!registry || typeof registry.get !== "function" || typeof registry.define !== "function") {
    return false;
  }
  if (!registry.get(WINDOW_MANAGER_TAG_NAME)) {
    registry.define(WINDOW_MANAGER_TAG_NAME, FishyMapWindowManagerElement);
  }
  return true;
}

registerFishyMapWindowManagerElement();
